import { FPSGame } from '../game/FPSGame';
import { WeaponType } from '../game/weapons/WeaponSystem';
import { fpsAudio } from '../game/audio/FPSAudio';

export class HUD {
  private game: FPSGame;
  private container: HTMLElement;

  private healthVal!: HTMLElement;
  private armorVal!: HTMLElement;
  private ammoVal!: HTMLElement;
  private currentWeaponName!: HTMLElement;
  private weaponSlots: Record<WeaponType, HTMLElement> = {} as any;
  private killfeedContainer!: HTMLElement;
  private bannerElement!: HTMLElement;
  private pickupNoticeElement!: HTMLElement;
  private scoreboardModal!: HTMLElement;
  private scoreboardBody!: HTMLElement;
  private lockPrompt!: HTMLElement;
  private matchTimerElement!: HTMLElement;
  private hitmarkerElement!: HTMLElement;

  // Death screen elements
  private deathOverlay!: HTMLElement;
  private deathKiller!: HTMLElement;
  private deathReason!: HTMLElement;
  private deathCountdown!: HTMLElement;
  private deathProgressFill!: HTMLElement;

  private pickupNoticeTimer: number = 0;
  private isTabPressed: boolean = false;

  constructor(game: FPSGame, container: HTMLElement) {
    this.game = game;
    this.container = container;

    this.createDomElements();
    this.setupListeners();

    // Hook pickup notification
    this.game.player.onPickupCollected = (text: string) => {
      this.showPickupNotice(text);
    };
  }

  private createDomElements() {
    this.container.innerHTML = `
      <!-- Crosshair -->
      <div id="crosshair">
        <div class="cross-dot"></div>
        <div class="cross-line top"></div>
        <div class="cross-line bottom"></div>
        <div class="cross-line left"></div>
        <div class="cross-line right"></div>
        <div id="hitmarker" class="hitmarker"></div>
      </div>

      <!-- Announcer Banner -->
      <div id="announcer-banner" class="announcer-banner"></div>

      <!-- Pickup Notice -->
      <div id="pickup-notice" class="pickup-notice"></div>

      <!-- Top Match Info -->
      <div class="top-bar">
        <div class="map-title">DM-MORPHEUS</div>
        <div id="match-timer" class="match-timer">10:00</div>
        <div class="frag-limit">FRAG LIMIT: 20</div>
      </div>

      <!-- Killfeed -->
      <div id="killfeed" class="killfeed"></div>

      <!-- Bottom HUD Panels -->
      <div class="bottom-hud">
        <!-- Health & Armor (Bottom Left) -->
        <div class="hud-panel stat-panel">
          <div class="stat-box health-box">
            <span class="stat-label">HEALTH</span>
            <span id="hud-health" class="stat-value">100</span>
          </div>
          <div class="stat-box armor-box">
            <span class="stat-label">SHIELD</span>
            <span id="hud-armor" class="stat-value">0</span>
          </div>
        </div>

        <!-- Weapon Quick Bar (Center Bottom) -->
        <div class="hud-panel weapon-bar">
          <div class="weapon-slot" data-weapon="enforcer"><span class="slot-num">1</span> ENF</div>
          <div class="weapon-slot" data-weapon="shock"><span class="slot-num">2</span> SHK</div>
          <div class="weapon-slot" data-weapon="pulse"><span class="slot-num">3</span> PLS</div>
          <div class="weapon-slot" data-weapon="flak"><span class="slot-num">4</span> FLK</div>
          <div class="weapon-slot" data-weapon="rocket"><span class="slot-num">5</span> RCK</div>
          <div class="weapon-slot" data-weapon="sniper"><span class="slot-num">6</span> SNP</div>
        </div>

        <!-- Ammo & Weapon Name (Bottom Right) -->
        <div class="hud-panel stat-panel right-stat">
          <div class="stat-box ammo-box">
            <span id="hud-weapon-name" class="stat-label">ENFORCER</span>
            <span id="hud-ammo" class="stat-value">50</span>
          </div>
        </div>
      </div>

      <!-- Scoreboard Modal (Tab) -->
      <div id="scoreboard" class="scoreboard-overlay hidden">
        <div class="scoreboard-card">
          <div class="scoreboard-header">
            <h2>UNREAL TOURNAMENT // DM-MORPHEUS</h2>
            <div class="scoreboard-sub">DEATHMATCH // LOW GRAVITY</div>
          </div>
          <table class="scoreboard-table">
            <thead>
              <tr>
                <th>RANK</th>
                <th>COMBATANT</th>
                <th>FRAGS</th>
                <th>DEATHS</th>
                <th>PING</th>
              </tr>
            </thead>
            <tbody id="scoreboard-body"></tbody>
          </table>
          <div class="scoreboard-footer">PRESS [TAB] TO CLOSE</div>
        </div>
      </div>

      <!-- Fullscreen Death Overlay -->
      <div id="death-overlay" class="death-overlay hidden">
        <div class="death-vignette"></div>
        <div class="death-panel">
          <div class="death-skull">☠</div>
          <h1 class="death-title">YOU WERE FRAGGED</h1>
          <div id="death-killer" class="death-killer">KILLED BY XAN KRIEGOR</div>
          <div id="death-reason" class="death-reason">[SHOCK RIFLE]</div>
          <div class="death-timer-box">
            <span id="death-countdown">RESPAWNING IN 3.0s</span>
            <div class="death-progress-bar"><div id="death-progress-fill" class="death-progress-fill"></div></div>
          </div>
          <div class="death-prompt">PRESS [SPACE] OR CLICK TO RESPAWN</div>
        </div>
      </div>

      <!-- Click to Play / Controls Overlay -->
      <div id="lock-prompt" class="lock-prompt">
        <div class="prompt-box">
          <h1 class="prompt-title">TOILLE FPS // MORPHEUS</h1>
          <p class="prompt-subtitle">UNREAL TOURNAMENT 99 DEATHMATCH</p>
          <div class="controls-grid">
            <div><span class="key">WASD</span> Move</div>
            <div><span class="key">SPACE</span> Low-G Jump</div>
            <div><span class="key">MOUSE 1</span> Primary Fire</div>
            <div><span class="key">MOUSE 2</span> Alt-Fire / Shock Combo</div>
            <div><span class="key">1-6 / WHEEL</span> Switch Weapons</div>
            <div><span class="key">TAB</span> Scoreboard</div>
            <div><span class="key">M</span> Mute Sound</div>
          </div>
          <button id="start-btn" class="start-btn">CLICK TO ENTER ARENA</button>
        </div>
      </div>
    `;

    // Query elements
    this.healthVal = this.container.querySelector('#hud-health')!;
    this.armorVal = this.container.querySelector('#hud-armor')!;
    this.ammoVal = this.container.querySelector('#hud-ammo')!;
    this.currentWeaponName = this.container.querySelector('#hud-weapon-name')!;
    this.killfeedContainer = this.container.querySelector('#killfeed')!;
    this.bannerElement = this.container.querySelector('#announcer-banner')!;
    this.pickupNoticeElement = this.container.querySelector('#pickup-notice')!;
    this.scoreboardModal = this.container.querySelector('#scoreboard')!;
    this.scoreboardBody = this.container.querySelector('#scoreboard-body')!;
    this.lockPrompt = this.container.querySelector('#lock-prompt')!;
    this.matchTimerElement = this.container.querySelector('#match-timer')!;
    this.hitmarkerElement = this.container.querySelector('#hitmarker')!;

    // Query death elements
    this.deathOverlay = this.container.querySelector('#death-overlay')!;
    this.deathKiller = this.container.querySelector('#death-killer')!;
    this.deathReason = this.container.querySelector('#death-reason')!;
    this.deathCountdown = this.container.querySelector('#death-countdown')!;
    this.deathProgressFill = this.container.querySelector('#death-progress-fill')!;

    const slots = this.container.querySelectorAll('.weapon-slot');
    slots.forEach((s) => {
      const type = (s as HTMLElement).dataset.weapon as WeaponType;
      this.weaponSlots[type] = s as HTMLElement;
    });
  }

  private setupListeners() {
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Tab') {
        e.preventDefault();
        this.isTabPressed = true;
      }
      if (e.code === 'KeyM') {
        fpsAudio.toggleMute();
      }
    });

    window.addEventListener('keyup', (e) => {
      if (e.code === 'Tab') {
        e.preventDefault();
        this.isTabPressed = false;
      }
    });

    const startBtn = this.container.querySelector('#start-btn');
    if (startBtn) {
      startBtn.addEventListener('click', () => {
        this.game.renderer.domElement.requestPointerLock();
      });
    }

    const deathOverlay = this.container.querySelector('#death-overlay');
    if (deathOverlay) {
      const respawnClick = () => {
        this.game.player.triggerRespawn();
        this.game.renderer.domElement.requestPointerLock();
      };
      deathOverlay.addEventListener('click', respawnClick);
      deathOverlay.addEventListener('mousedown', respawnClick);
    }
  }

  public showPickupNotice(text: string) {
    this.pickupNoticeElement.textContent = text;
    this.pickupNoticeElement.classList.add('visible');
    this.pickupNoticeTimer = 2.0;
  }

  public update(deltaTime: number) {
    // 1. Lock Prompt visibility (hidden when locked or while dead so death screen has full priority)
    if (this.game.player.isLocked || !this.game.player.isAlive) {
      this.lockPrompt.classList.add('hidden');
    } else {
      this.lockPrompt.classList.remove('hidden');
    }

    // 2. Health & Armor
    const player = this.game.player;
    this.healthVal.textContent = Math.max(0, Math.ceil(player.health)).toString();
    this.armorVal.textContent = Math.max(0, Math.ceil(player.armor)).toString();

    if (player.health < 30) {
      this.healthVal.classList.add('low-health');
    } else {
      this.healthVal.classList.remove('low-health');
    }

    // 3. Ammo & Current Weapon
    const curWep = player.weapons[player.currentWeapon];
    this.ammoVal.textContent = curWep.ammo.toString();
    this.currentWeaponName.textContent = curWep.name.toUpperCase();

    // 4. Weapon Quick Bar
    for (const [type, el] of Object.entries(this.weaponSlots)) {
      const wep = player.weapons[type as WeaponType];
      if (wep.hasWeapon) {
        el.classList.add('unlocked');
      } else {
        el.classList.remove('unlocked');
      }

      if (player.currentWeapon === type) {
        el.classList.add('active');
      } else {
        el.classList.remove('active');
      }
    }

    // 5. Match Timer
    const mins = Math.floor(this.game.matchTime / 60);
    const secs = Math.floor(this.game.matchTime % 60);
    this.matchTimerElement.textContent = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

    // 6. Killfeed
    this.updateKillfeed();

    // 7. Announcer Banner
    if (this.game.bannerMessage) {
      this.bannerElement.textContent = this.game.bannerMessage;
      this.bannerElement.classList.add('active');
    } else {
      this.bannerElement.classList.remove('active');
    }

    // 8. Pickup notice
    if (this.pickupNoticeTimer > 0) {
      this.pickupNoticeTimer -= deltaTime;
      if (this.pickupNoticeTimer <= 0) {
        this.pickupNoticeElement.classList.remove('visible');
      }
    }

    // 9. Death Overlay update
    if (!player.isAlive) {
      this.deathOverlay.classList.remove('hidden');
      const killer = player.killerName || 'The Abyss';
      this.deathKiller.textContent = player.killerName ? `KILLED BY ${player.killerName.toUpperCase()}` : 'YOU FELL INTO THE ABYSS';
      this.deathReason.textContent = player.deathReason ? `[${player.deathReason.toUpperCase()}]` : '[FALL DAMAGE]';
      const remaining = Math.max(0, player.respawnTimer).toFixed(1);
      this.deathCountdown.textContent = `RESPAWNING IN ${remaining}s`;
      const fillPct = Math.max(0, Math.min(100, ((3.0 - player.respawnTimer) / 3.0) * 100));
      this.deathProgressFill.style.width = `${fillPct}%`;
    } else {
      this.deathOverlay.classList.add('hidden');
    }

    // 10. Scoreboard (Visible on Tab or when match is over)
    if (this.isTabPressed || this.game.isMatchOver) {
      this.scoreboardModal.classList.remove('hidden');
      this.renderScoreboard();
    } else {
      this.scoreboardModal.classList.add('hidden');
    }
  }

  private updateKillfeed() {
    this.killfeedContainer.innerHTML = '';
    for (const item of this.game.killfeed) {
      const row = document.createElement('div');
      row.className = 'kill-item';
      row.innerHTML = `
        <span class="killer ${item.killer === 'Player' ? 'is-self' : ''}">${item.killer}</span>
        <span class="weapon-tag">[${item.weapon}]</span>
        <span class="victim ${item.victim === 'Player' ? 'is-self' : ''}">${item.victim}</span>
      `;
      this.killfeedContainer.appendChild(row);
    }
  }

  private renderScoreboard() {
    const list = this.game.getScoreboard();
    this.scoreboardBody.innerHTML = '';

    list.forEach((entry, idx) => {
      const tr = document.createElement('tr');
      if (entry.isPlayer) tr.classList.add('player-row');
      tr.innerHTML = `
        <td>#${idx + 1}</td>
        <td><strong>${entry.name}</strong></td>
        <td class="frags-col">${entry.frags}</td>
        <td>${entry.deaths}</td>
        <td>${entry.ping}ms</td>
      `;
      this.scoreboardBody.appendChild(tr);
    });
  }
}
